/**
 * A2: the app is back in front of someone. Timers stop while an iPad sleeps
 * or the app sits in the background, so what depends on time looks again
 * here: the idle lock, the customer's signing screen, a device store that
 * could not be read while the iPad was locked.
 *
 * Two signals feed one list, and a screen listens once:
 * - the page's own `visibilitychange`, in every build;
 * - in the iPad app, the shell's `appStateChange` (native/install.ts), since
 *   WKWebView on iPad does not always send the first (WebKit bug 207256).
 *
 * Both may fire for one return: every listener must be safe to call twice.
 * This file is the only one that listens to `visibilitychange` (a unit test
 * keeps it so): a screen that listened itself would miss the shell's signal.
 */
type Listener = () => void

const listeners = new Set<Listener>()
let listening = false

/** Calls `listener` whenever the app comes back to the foreground; returns how to stop. */
export function onResume(listener: Listener): () => void {
  listenToPage()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The app came back: every listener looks again. */
export function resumed(): void {
  for (const listener of [...listeners]) listener()
}

function listenToPage() {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resumed()
  })
}
